import { useId, useState } from 'react';
import { apiFetch } from '~/lib/api';
import { Icon } from '~/components/ui';
import TermsCheckbox from '~/components/TermsCheckbox';
import { useT } from '~/i18n';
import { TBIT_JOBS_TERMS_VERSION, readTermsError, termsAcceptanceBody } from '~/lib/termsVersions';
import { usePlanCtaCopy, type PlanCtaCase, type PlanCtaSurface } from '../lib/planCta';
import { validateCoupon } from '../api/couponClient';
import { ReferralConsentNotice } from './ReferralConsentNotice';
import { describeDiscount, type ValidCoupon } from '../lib/coupon';
import {
  PLAN_PRICE,
  discountMonths,
  discountedPriceCents,
  formatPlanCents,
  planCadence,
  type PartnerDiscount,
} from '../lib/plan';

/**
 * La oferta del plan. UNA sola, usada en los tres sitios donde aparece: el muro
 * que se descubre tras la tercera demostración, el modal de cualquier "Enviar"
 * posterior, y la tab Plan del perfil.
 *
 * El titular cambia según dónde esté (no es lo mismo descubrirlo que renovarlo),
 * pero el precio, los beneficios y el botón son los mismos en los tres — que es
 * exactamente lo que se rompía cuando cada pantalla escribía su propia versión.
 *
 * Los beneficios están en el orden en que le importan: primero lo que pasa
 * mientras él no está, después dónde le llega la respuesta, y de último el
 * trabajo sobre su hoja de vida.
 *
 * ── EL CÓDIGO DE DESCUENTO ─────────────────────────────────────────────────
 *
 * Escondido tras un enlace y no como un campo más: la gran mayoría llega sin
 * código, y un campo vacío rotulado "cupón" delante de alguien que no tiene
 * ninguno le enseña que hay un precio mejor al que no está accediendo. Quien sí
 * lo tiene lo está buscando y encuentra el enlace.
 *
 * Se valida ANTES de Stripe —y no dejándoselo al campo de la página de pago—
 * porque el descuento es lo que convence: verlo confirmado antes de sacar la
 * tarjeta es la diferencia entre una promesa y un hecho. Por eso el mensaje va
 * en mint: salió de una respuesta del servidor, no de lo que el candidato
 * tecleó.
 *
 * ── EL PRECIO CON DESCUENTO ────────────────────────────────────────────────
 *
 * Con un cupón validado, o con el descuento del socio que lo trajo
 * (`partnerDiscount`, que el checkout aplica solo), el recuadro muestra el
 * precio de lista tachado y el rebajado al lado. Antes no se recalculaba ("el
 * total lo muestra Stripe"), pero quien llega por un socio tiene que VER su
 * beneficio antes de sacar la tarjeta: es lo que el socio le prometió. El
 * número sale de la misma regla que aplica Stripe (`discountedPriceCents`). El
 * cupón tecleado manda sobre el del socio, igual que en el checkout.
 *
 * Y se dice por cuánto tiempo (`discountMonths`): el del socio es sólo el
 * primer mes, así que el recuadro dice «USD 23 el primer mes» y «Después, USD
 * 30 al mes», nunca un USD 23 al mes que no se va a cobrar.
 *
 * ── LOS TÉRMINOS ───────────────────────────────────────────────────────────
 *
 * Una casilla OBLIGATORIA «Acepto los Términos y condiciones de TBit Jobs»
 * sobre el botón (2026-10-08). El servidor no abre checkout sin ella, y guarda
 * cada aceptación con su versión. El botón no se apaga en silencio: sin la
 * casilla, el clic dice qué falta. Si el servidor ya va por otra versión
 * (`terms_outdated`), se adopta la suya, se desmarca y se vuelve a preguntar.
 */

// El texto de cada beneficio vive en el diccionario (`plan.offer.benefits`),
// con la misma clave que su ícono.
const BENEFITS = ['send', 'mail', 'doc'] as const;

// Los errores se guardan como clave y no como texto: así un cambio de idioma
// con el mensaje en pantalla lo traduce en vez de dejarlo en el anterior.
type CouponError = 'expired' | 'unavailable';
type CheckoutError = 'alreadySubscribed' | 'checkoutUnavailable' | 'checkoutFailed' | 'network';
type TermsProblem = 'required' | 'outdated';

interface Props {
  headline: string;
  lede: string;
  /**
   * El llamado que corresponde (lo decide el servidor, `cta_case`): pone el
   * texto del botón. Sólo con `trial` se ofrece la prueba y las condiciones se
   * dicen enteras debajo; en los otros casos no se menciona prueba.
   */
  ctaCase: PlanCtaCase;
  /** Dónde está esta oferta; viaja al checkout como `cta_surface`. */
  surface: PlanCtaSurface;
  /** False dentro del modal, que ya trae su propio marco. */
  framed?: boolean;
  /**
   * El checkout contestó 409 `already_subscribed`: Stripe dice que la
   * suscripción sigue viva y lo que está viejo es lo que tenemos pintado. Quien
   * monta la oferta vuelve a pedir sus datos (sin tapar la pantalla, para que el
   * mensaje se alcance a leer).
   */
  onAlreadySubscribed?: () => void;
  /** El descuento del socio que lo trajo (del backend). El checkout lo aplica solo. */
  partnerDiscount?: PartnerDiscount | null;
  /**
   * 'compare': dos columnas (lo que ya tiene / el plan como tarjeta premium) en
   * vez de la oferta de siempre. La misma lógica de pago: solo cambia el marco.
   * `headline` pasa a ser el titular de la pareja y `lede` no se pinta.
   */
  variant?: 'compare';
}

export default function PlanOffer({
  headline,
  lede,
  ctaCase,
  surface,
  framed = true,
  onAlreadySubscribed,
  partnerDiscount = null,
  variant,
}: Props) {
  const t = useT('plan');
  const cta = usePlanCtaCopy(ctaCase);
  const trialEligible = ctaCase === 'trial';
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<CheckoutError | null>(null);

  // useId y no un id fijo: el panel puede tener dos ofertas a la vez (la del
  // muro y la del modal), y un `htmlFor` repetido apunta a la casilla de la otra.
  const termsId = useId();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsVersion, setTermsVersion] = useState(TBIT_JOBS_TERMS_VERSION);
  const [termsProblem, setTermsProblem] = useState<TermsProblem | null>(null);

  const [couponOpen, setCouponOpen] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [checking, setChecking] = useState(false);
  const [applied, setApplied] = useState<ValidCoupon | null>(null);
  const [couponError, setCouponError] = useState<CouponError | null>(null);

  const handleApplyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code || checking) return;

    setCouponError(null);
    setChecking(true);
    const state = await validateCoupon(code);
    setChecking(false);

    if (state.status === 'valid') {
      setApplied(state.coupon);
      return;
    }
    setApplied(null);
    setCouponError(state.status === 'invalid' ? 'expired' : 'unavailable');
  };

  const handleRemoveCoupon = () => {
    setApplied(null);
    setCouponInput('');
    setCouponError(null);
  };

  const handleCheckout = async () => {
    setError(null);
    if (!termsAccepted) {
      // Lo que falta se dice junto a la casilla; el pedido ni sale.
      setTermsProblem((current) => current ?? 'required');
      return;
    }
    setStarting(true);
    try {
      // Siempre con la aceptación de los términos (la versión que se mostró);
      // el cupón, sólo si hay uno validado.
      const init: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...termsAcceptanceBody(termsVersion),
          cta_surface: surface,
          ...(applied ? { promotion_code_id: applied.promotion_code_id } : {}),
        }),
      };

      const res = await apiFetch('/job-apply/api/create-checkout', init);
      const body = (await res.json().catch(() => ({}))) as {
        url?: string;
        code?: string;
        error?: string;
        current_version?: string;
      };

      if (res.ok && body.url) {
        window.location.href = body.url;
        return;
      }
      const termsError = readTermsError(res.status, body);
      if (termsError) {
        // Los términos cambiaron (o el servidor no vio la casilla): se desmarca
        // y se vuelve a preguntar, ya con la versión que el servidor exige.
        if (termsError.current_version) setTermsVersion(termsError.current_version);
        setTermsAccepted(false);
        setTermsProblem(termsError.code === 'terms_outdated' ? 'outdated' : 'required');
        return;
      }
      if (body.error === 'invalid_promotion_code' || body.code === 'invalid_promotion_code') {
        // El cupón murió entre la validación y el clic (o se agotó). Se quita y
        // se dice dónde: el pago sigue disponible sin él, a un clic más, y así
        // nadie termina en Stripe pagando completo sin haberlo decidido.
        setApplied(null);
        setCouponOpen(true);
        setCouponError('expired');
      } else if (body.code === 'already_subscribed' || body.error === 'already_subscribed') {
        setError('alreadySubscribed');
        onAlreadySubscribed?.();
      } else if (body.code === 'checkout_unavailable') {
        // Es un problema de configuración nuestro, no de su tarjeta. Decirle
        // "intenta de nuevo" lo mandaría a repetir algo que va a fallar igual.
        setError('checkoutUnavailable');
      } else {
        setError('checkoutFailed');
      }
    } catch {
      setError('network');
    } finally {
      setStarting(false);
    }
  };

  // El cupón tecleado manda sobre el del socio (misma regla que el checkout).
  const discount = applied ?? partnerDiscount;
  const discountedCents = discountedPriceCents(discount);
  const discountedPrice = discountedCents === null ? null : formatPlanCents(discountedCents);
  // Por cuántos meses pagados rige: 'forever' o un número (el del socio, 1).
  const months = discount ? discountMonths(discount) : 'forever';
  const temporary = Boolean(discountedPrice) && months !== 'forever';
  // Lo que se dice del cobro tras la prueba. Un `once` tecleado se gasta en la
  // factura de USD 0 del inicio de la prueba, así que el día 8 ya es precio de
  // lista; un `repeating` (el del socio) sí cubre el cobro del día 8.
  const trialCharge = !discountedPrice
    ? t.offer.trial.charge(PLAN_PRICE)
    : months === 'forever'
      ? t.offer.trial.charge(discountedPrice)
      : applied?.duration === 'once'
        ? t.offer.trial.charge(PLAN_PRICE)
        : t.offer.trial.chargeIntro(discountedPrice, months, PLAN_PRICE);

  const coupon = (
    <div className="mb-4">
      {!couponOpen && !applied ? (
        <button
          type="button"
          className="btn btn-ghost btn-sm px-0 text-sm hush"
          onClick={() => setCouponOpen(true)}
        >
          {t.coupon.open}
        </button>
      ) : (
        <div className="space-y-2">
          {applied ? (
            <div className="flex items-center gap-2 flex-wrap">
              {/* Mint: es un descuento CONFIRMADO contra Stripe, no una promesa. */}
              <p className="text-sm font-semibold" style={{ color: 'var(--mint-ink)' }}>
                {describeDiscount(applied)}
              </p>
              <button type="button" className="btn btn-ghost btn-sm" onClick={handleRemoveCoupon}>
                {t.coupon.remove}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <label className="sr-only" htmlFor="plan-coupon">
                {t.coupon.label}
              </label>
              <input
                id="plan-coupon"
                className="input input-sm flex-1 min-w-0"
                value={couponInput}
                maxLength={24}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                placeholder={t.coupon.placeholder}
                // Mayúsculas en el estado y no sólo en CSS: es lo que se manda.
                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleApplyCoupon();
                  }
                }}
              />
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={handleApplyCoupon}
                disabled={checking || !couponInput.trim()}
              >
                {checking && <span className="loading loading-spinner loading-xs" />}
                {t.coupon.apply}
              </button>
            </div>
          )}

          {/* Consentimiento (Ley 1581). Validar un código de socio YA lo deja
              como tu referidor, así que antes del clic se dice qué implica, y
              después, con el nombre del socio. */}
          {!applied && <p className="text-xs hush">{t.coupon.partnerHint}</p>}
          {applied?.partner && (
            <ReferralConsentNotice who={applied.partner.partner_name ?? applied.partner.code} className="hush" />
          )}

          {couponError && (
            <p className="text-sm" style={{ color: 'var(--coral-ink)' }}>
              {t.coupon[couponError]}
            </p>
          )}
        </div>
      )}
    </div>
  );

  const intro = (
    <>
      <span className="brand-label">{t.yourPlan}</span>
      <h3 className="mt-1 mb-2 text-lg font-bold" style={{ letterSpacing: '-0.02em' }}>
        {headline}
      </h3>
      <p className="text-sm hush mb-4">{lede}</p>

      <ul className="space-y-2 mb-4">
        {BENEFITS.map((benefit) => (
          <li key={benefit} className="flex items-start gap-2 text-sm">
            <span className="text-brand mt-0.5 shrink-0">
              <Icon name={benefit} size={16} />
            </span>
            <span>{t.offer.benefits[benefit]}</span>
          </li>
        ))}
      </ul>
    </>
  );

  const checkout = (
    <>
      <div
        className="rounded-2xl px-4 py-3 mb-4"
        style={{ background: 'var(--surface-hi)', border: '1px solid var(--hairline)' }}
      >
        {trialEligible && (
          <p className="text-sm font-semibold mb-1" style={{ color: 'var(--mint-ink)' }}>
            {t.offer.trial.badge}
          </p>
        )}
        <div className="flex items-baseline gap-2">
          {discountedPrice ? (
            <>
              <s className="text-sm hush" aria-label={t.offer.listPrice(PLAN_PRICE)}>{PLAN_PRICE}</s>
              {/* Mint: el descuento es un hecho (del servidor o de Stripe). */}
              <span className="text-xl font-bold" style={{ letterSpacing: '-0.02em', color: 'var(--mint-ink)' }}>
                {discountedPrice}
              </span>
            </>
          ) : (
            <span className="text-xl font-bold" style={{ letterSpacing: '-0.02em' }}>
              {PLAN_PRICE}
            </span>
          )}
          <span className="text-sm hush">
            {temporary && typeof months === 'number' ? t.offer.firstMonths(months) : planCadence()}
          </span>
        </div>
        {temporary && <p className="text-sm hush mt-1">{t.offer.after(PLAN_PRICE)}</p>}
        {!applied && partnerDiscount && discountedPrice && (
          <>
            <p className="text-sm mt-1" style={{ color: 'var(--mint-ink)' }}>
              {t.offer.partner(
                partnerDiscount.code,
                formatPlanCents(partnerDiscount.amount_off),
                discountMonths(partnerDiscount),
              )}
            </p>
            {/* Junto al beneficio, no en letra chica aparte: es lo que le da
                el socio y lo que el socio ve a cambio. */}
            <ReferralConsentNotice
              who={partnerDiscount.partner_name ?? partnerDiscount.code}
              className="hush mt-1"
            />
          </>
        )}
      </div>

      {coupon}

      <TermsCheckbox
        id={termsId}
        className="mb-3"
        checked={termsAccepted}
        onChange={(checked) => {
          setTermsAccepted(checked);
          if (checked) setTermsProblem(null);
        }}
        before={t.offer.terms.before}
        label={t.offer.terms.label}
        href={t.offer.terms.href}
        after={t.offer.terms.after}
        notice={termsProblem === 'outdated' ? t.offer.terms.outdated : null}
        error={termsProblem === 'required' ? t.offer.terms.required : null}
      />

      <button className="btn btn-primary w-full" onClick={handleCheckout} disabled={starting}>
        {starting && <span className="loading loading-spinner loading-sm" />}
        {cta.label}
      </button>
      {trialEligible ? (
        // Las condiciones de la prueba, a tamaño de lectura y no en letra chica:
        // quien deja la tarjeta tiene que saber cuándo y cuánto se le cobra.
        <div className="text-sm mt-3 space-y-1" data-testid="trial-terms">
          <p>{t.offer.trial.card}</p>
          <p>{trialCharge}</p>
          <p>{t.offer.trial.cancel}</p>
          <p className="text-xs hush">{t.offer.trial.secureNote}</p>
        </div>
      ) : (
        <p className="text-xs hush text-center mt-2">{t.offer.secureNote}</p>
      )}
      {error && (
        <p className="text-sm mt-3" style={{ color: 'var(--coral-ink)' }}>
          {t.offer.errors[error]}
        </p>
      )}
    </>
  );

  const body = (
    <>
      {intro}
      {checkout}
    </>
  );

  if (variant === 'compare') {
    return (
      <div className="space-y-4 rise">
        <div>
          <span className="brand-label">{t.yourPlan}</span>
          <h3 className="mt-1 text-lg font-bold" style={{ letterSpacing: '-0.02em' }}>
            {headline}
          </h3>
        </div>
        <div className="grid gap-3 md:grid-cols-2 items-start">
          <div
            className="rounded-2xl p-4 space-y-2"
            style={{ background: 'var(--surface-hi)', border: '1px solid var(--hairline)' }}
          >
            <div className="flex items-center justify-between gap-2">
              <b>{t.page.compare.have.title}</b>
              {/* Mint: es un hecho (ya lo tiene), no una promesa. */}
              <span className="text-xs font-semibold" style={{ color: 'var(--mint-ink)' }}>
                {t.page.compare.have.chip}
              </span>
            </div>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              {t.page.compare.have.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="text-xs hush">{t.page.compare.have.footer}</p>
          </div>

          <div
            className="rounded-2xl overflow-hidden relative px-4 pb-4"
            style={{
              background: 'linear-gradient(165deg, var(--navy) 0%, #3a2c9c 62%, #5b34c9 100%)',
              color: '#fff',
              boxShadow: '0 26px 50px -28px rgba(91,52,201,.85), 0 0 0 1px rgba(218,194,254,.35) inset',
            }}
          >
            <div className="relative -mx-4 overflow-hidden" style={{ height: 150 }}>
              <img
                src="/landing/hotel-housekeeping.webp"
                alt={t.page.compare.photoAlt}
                className="w-full h-full block"
                style={{ objectFit: 'cover', objectPosition: '50% 22%' }}
              />
              <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{ background: 'linear-gradient(180deg, rgba(44,48,128,0) 35%, rgba(44,48,128,.96) 100%)' }}
              />
              <span
                className="absolute font-bold uppercase"
                style={{
                  top: 16,
                  right: -38,
                  transform: 'rotate(35deg)',
                  background: 'var(--lila)',
                  color: 'var(--navy)',
                  fontSize: '.6rem',
                  lineHeight: 1,
                  letterSpacing: '.12em',
                  padding: '7px 44px',
                  zIndex: 1,
                  boxShadow: '0 6px 14px -6px rgba(20,23,63,.5)',
                }}
              >
                {t.page.compare.ribbon}
              </span>
              <div className="absolute flex items-center gap-2" style={{ left: 14, bottom: 10, zIndex: 1 }}>
                <img src="/brand/mascot-96.png" alt="" width={30} height={30} style={{ objectFit: 'contain' }} />
                <span className="text-xs">{t.page.compare.caption}</span>
              </div>
            </div>
            <div className="mt-3">
              <b>{t.page.compare.planTitle}</b>
            </div>
            <ul className="list-disc pl-5 space-y-1 text-sm my-3" style={{ color: '#fff' }}>
              {t.page.compare.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
            {/* El pago, claro sobre el fondo oscuro: precio, cupón, términos y botón. */}
            <div
              className="rounded-xl p-3"
              style={{ background: 'var(--surface)', color: 'var(--ink)' }}
              data-testid="compare-checkout"
            >
              {checkout}
            </div>
          </div>
        </div>
        <p
          className="text-xs rounded-2xl px-3 py-2"
          style={{ border: '1px dashed var(--hairline-hi)', color: 'var(--ink-soft)' }}
        >
          {t.page.compare.never}
        </p>
      </div>
    );
  }

  if (!framed) return <div>{body}</div>;

  return (
    <div
      className="card rise"
      style={{
        borderColor: 'var(--hairline-hi)',
        background: 'linear-gradient(150deg, rgba(218,194,254,0.28) 0%, var(--surface) 58%)',
      }}
    >
      <div className="card-body">{body}</div>
    </div>
  );
}
